/**
 * Format a date into a human-readable time string
 */
export const formatTime = (date: Date): string => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const messageDate = new Date(date);
  const messageDay = new Date(messageDate.getFullYear(), messageDate.getMonth(), messageDate.getDate());

  // Check if the message is from today
  if (messageDay.getTime() === today.getTime()) {
    return messageDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  // Check if the message is from yesterday
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (messageDay.getTime() === yesterday.getTime()) {
    return 'Yesterday';
  }

  // Otherwise, return the date
  return messageDate.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

/**
 * Format a date into a relative time string (e.g. "2 hours ago", "Just now", etc.)
 */
export const formatRelativeTime = (date: Date): string => {
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return 'Just now';
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes} min${diffInMinutes > 1 ? 's' : ''} ago`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
  }

  return formatTime(date);
};

/**
 * Truncate text to a specific length and add ellipsis
 */
export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
};

/**
 * Get initials from a name
 */

export const getInitials = (name: string | null | undefined, fallback: string = '??'): string => {
  try {
    // 1. Handle null/undefined/empty input
    if (!name || typeof name !== 'string' || name.trim().length === 0) {
      return fallback;
    }

    // 2. Clean the name:
    //    - Replace common separators (-, _, .) with spaces.
    //    - Insert a space before uppercase letters following lowercase letters (for camelCase).
    //    - Remove any characters that are not letters, numbers, or spaces.
    //    - Trim whitespace from start/end.
    const cleanedName = name
      .replace(/[-_\.]+/g, ' ') // Replace separators with a single space
      .replace(/([a-z])([A-Z])/g, '$1 $2') // Add space for camelCase: saiSarath -> sai Sarath
      .replace(/[^a-zA-Z0-9\s]/g, '') // Remove non-alphanumeric characters (excluding space)
      .trim();

    // 3. Split into parts based on spaces
    const parts = cleanedName.split(/\s+/).filter(part => part.length > 0); // Split by one or more spaces and remove empty parts

    // 4. Generate initials
    let initials = '';

    if (parts.length === 0) {
      // If no valid parts found after cleaning (e.g., name was " _.- ")
      return fallback;
    } else if (parts.length === 1) {
      // If only one part (e.g., "Sai", "S", "Sathish")
      const singlePart = parts[0];
      initials = singlePart.substring(0, 2); // Take the first two characters
    } else {
      // If multiple parts (e.g., ["sai", "Sarath"], ["Sai", "Sarath", "Kumar"])
      initials = parts[0].charAt(0) + parts[1].charAt(0); // Take the first char of the first two parts
    }

    // 5. Final formatting: Uppercase and ensure it's not empty
    const finalInitials = initials.toUpperCase();

    return finalInitials.length > 0 ? finalInitials : fallback;
  } catch (error: any) {
    console.error('Error getting initials:', error);
    return name
      ?.split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2) || fallback;
  }
};


export const getUserId = () => {
  return localStorage.getItem('userId') || '';
}

export const getUserName = () => {
  return localStorage.getItem('userName') || '';
}

export const getCustomer = () => {
  return localStorage.getItem('customer') || '';
}

export const getAgentId = () => {
  return localStorage.getItem('agentId') || '';
}

export const getAgentUniqId = () => {
  return localStorage.getItem('agentUniqId') || '';
}
